import {
  Almanac,
  Category,
  Role,
  roleToJSON,
} from "protos/robot_syncer/plant_profile";
import { AlmanacMostSimilarFields } from "portal/components/categoryTemplates/createCategory/AlmanacMostSimilarFields";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Skeleton,
  Typography,
} from "@mui/material";
import { type CreateCategoryTemplateArgs } from "portal/components/categoryTemplates/createCategory/useCreateCategoryTemplate";
import {
  defaultMostSimilarTo,
  type PlantProfileRoleOption,
  translateCategoryName,
} from "portal/utils/categoryCollection/category";
import { Field, Form, Formik, type FormikConfig } from "formik";
import { isValidAlmanac } from "portal/utils/shootTime";
import { object, string } from "yup";
import { resolveAlmanacCategory } from "portal/components/categoryTemplates/createCategory/resolveAlmanacCategory";
import { TextField } from "formik-mui";
import { useAlmanacOptions } from "portal/components/categoryTemplates/useAlmanacOptions";
import { useTranslation } from "react-i18next";
import React, { ReactNode, useMemo } from "react";

interface CopyCategoryToLibraryDialogProps {
  category: Category;
  profileName: string;
  role: Role;
  customerUuid?: string;
  createCategoryTemplate: (
    args: CreateCategoryTemplateArgs
  ) => Promise<string | undefined>;
  onClose: () => void;
}

type CopyCategoryNameValues = {
  name: string;
};

type CopyCategoryAlmanacValues = CopyCategoryNameValues & {
  sourceAlmanacId: string;
  mostSimilarTo: string;
};

const createTemplateAndClose = async (
  createCategoryTemplate: CopyCategoryToLibraryDialogProps["createCategoryTemplate"],
  category: Category,
  name: string,
  almanac: Almanac,
  onClose: () => void
): Promise<void> => {
  const createdId = await createCategoryTemplate({
    name: name.trim(),
    chipIds: category.chipIds,
    almanac,
  });
  if (createdId) {
    onClose();
  }
};

export const CopyCategoryToLibraryDialog = (
  props: CopyCategoryToLibraryDialogProps
): React.JSX.Element => {
  const { t } = useTranslation();
  return (
    <Dialog
      open
      onClose={props.onClose}
      slotProps={{ paper: { className: "min-w-1/3" } }}
    >
      <DialogTitle>
        {t("components.categoryCollectionProfile.actions.addToCategoryLibrary")}
      </DialogTitle>
      {isValidAlmanac(props.category.almanac) ? (
        <CopyCategoryToLibraryNameForm {...props} />
      ) : (
        <CopyCategoryToLibraryAlmanacForm {...props} />
      )}
    </Dialog>
  );
};

const CopyCategoryToLibraryNameForm = ({
  category,
  profileName,
  createCategoryTemplate,
  onClose,
}: CopyCategoryToLibraryDialogProps): React.JSX.Element => {
  const { t } = useTranslation();
  return (
    <CopyCategoryToLibraryForm
      categoryName={category.name}
      profileName={profileName}
      initialValues={{ name: category.name }}
      validationSchema={object({
        name: string().required(t("utils.form.required")),
      })}
      onClose={onClose}
      onSubmit={async ({ name }) => {
        if (!isValidAlmanac(category.almanac)) {
          return;
        }
        await createTemplateAndClose(
          createCategoryTemplate,
          category,
          name,
          category.almanac,
          onClose
        );
      }}
    />
  );
};

const CopyCategoryToLibraryAlmanacForm = ({
  category,
  profileName,
  role,
  customerUuid,
  createCategoryTemplate,
  onClose,
}: CopyCategoryToLibraryDialogProps): React.JSX.Element => {
  const { t } = useTranslation();
  const { almanacGroups, defaultAlmanacId, isLoading } =
    useAlmanacOptions(customerUuid);

  const initialValues: CopyCategoryAlmanacValues = useMemo(
    () => ({
      name: category.name,
      sourceAlmanacId: defaultAlmanacId,
      mostSimilarTo: defaultMostSimilarTo(
        category.name,
        roleToJSON(role) as PlantProfileRoleOption
      ),
    }),
    [category.name, defaultAlmanacId, role]
  );

  return (
    <CopyCategoryToLibraryForm
      categoryName={category.name}
      profileName={profileName}
      initialValues={initialValues}
      isLoading={isLoading}
      validationSchema={object({
        name: string().required(t("utils.form.required")),
        mostSimilarTo: string().required(t("utils.form.required")),
        sourceAlmanacId: string().required(t("utils.form.required")),
      })}
      onClose={onClose}
      onSubmit={async (
        { name, mostSimilarTo, sourceAlmanacId },
        { setFieldError }
      ) => {
        const resolved = resolveAlmanacCategory(
          almanacGroups,
          sourceAlmanacId,
          mostSimilarTo,
          t
        );
        if (!resolved.ok) {
          setFieldError(resolved.field, resolved.message);
          return;
        }
        await createTemplateAndClose(
          createCategoryTemplate,
          category,
          name,
          resolved.almanac,
          onClose
        );
      }}
    >
      <AlmanacMostSimilarFields
        almanacGroups={almanacGroups}
        defaultAlmanacId={defaultAlmanacId}
      />
    </CopyCategoryToLibraryForm>
  );
};

type CopyCategoryToLibraryFormProps<Values extends CopyCategoryNameValues> = {
  categoryName: string;
  profileName: string;
  initialValues: Values;
  validationSchema: FormikConfig<Values>["validationSchema"];
  onSubmit: FormikConfig<Values>["onSubmit"];
  onClose: () => void;
  isLoading?: boolean;
  children?: ReactNode;
};

const CopyCategoryToLibraryForm = <Values extends CopyCategoryNameValues>({
  categoryName,
  profileName,
  initialValues,
  validationSchema,
  onSubmit,
  onClose,
  isLoading,
  children,
}: CopyCategoryToLibraryFormProps<Values>): React.JSX.Element => {
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <DialogContent>
        <div className="flex flex-col gap-4 pt-2">
          <Skeleton variant="rectangular" className="h-14 w-full rounded" />
        </div>
      </DialogContent>
    );
  }

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
    >
      {({ submitForm, isSubmitting }) => (
        <Form>
          <DialogContent>
            <div className="flex flex-col gap-4 pt-2">
              <Typography>
                {t(
                  "components.categoryCollectionProfile.actions.addToCategoryLibraryConfirm",
                  {
                    categoryName: translateCategoryName(t, categoryName),
                    profileName,
                  }
                )}
              </Typography>
              <Field
                component={TextField}
                name="name"
                label={t("components.categoryTemplates.fields.name")}
                fullWidth
              />
              {children}
            </div>
          </DialogContent>
          <DialogActions>
            <Button
              variant="text"
              color="inherit"
              disabled={isSubmitting}
              onClick={onClose}
            >
              {t("utils.actions.cancel")}
            </Button>
            <Button
              variant="contained"
              color="primary"
              loading={isSubmitting}
              onClick={submitForm}
            >
              {t("utils.actions.add")}
            </Button>
          </DialogActions>
        </Form>
      )}
    </Formik>
  );
};
